import config from '@/config';
import fetchWrapper from '@/services/fetchWrapper.service';
import { getPreferredLocale } from '@/services/locale.service';

function serviceWorkerUrl() {
  return `${process.env.PUBLIC_URL || ''}/service-worker.js`;
}

function isPushSupported() {
  return typeof window !== 'undefined'
    && 'Notification' in window
    && 'PushManager' in window
    && 'serviceWorker' in navigator;
}

function getPublicKey() {
  return String(config.webPush?.publicKey || '').trim();
}

function toApplicationServerKey(publicKey) {
  const padded = `${publicKey}${'='.repeat((4 - (publicKey.length % 4)) % 4)}`;
  const base64 = padded.replace(/-/g, '+').replace(/_/g, '/');
  const bytes = window.atob(base64);
  return Uint8Array.from(bytes, character => character.charCodeAt(0));
}

async function getRegistration() {
  if (!isPushSupported()) {
    throw new Error('Push notifications are not supported by this browser.');
  }

  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) {
    return existing;
  }
  return await navigator.serviceWorker.register(serviceWorkerUrl());
}

async function saveSubscription(subscription) {
  const { response, data } = await fetchWrapper.post(
    `${config.apis.api.url}/notification/push-subscriptions`,
    {
      injectToken: true,
      body: {
        subscription: subscription.toJSON(),
        locale: getPreferredLocale(),
      },
    }
  );

  if (!response.ok) {
    throw new Error(data?.error_message || 'Unable to enable push notifications.');
  }
}

export async function syncPushSubscription() {
  if (!isPushSupported() || !getPublicKey() || Notification.permission !== 'granted') {
    return null;
  }

  const registration = await getRegistration();
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    return null;
  }
  await saveSubscription(subscription);
  return subscription;
}

export async function getPushNotificationState() {
  if (!isPushSupported()) {
    return 'unsupported';
  }
  if (!getPublicKey()) {
    return 'unavailable';
  }
  if (Notification.permission === 'denied') {
    return 'blocked';
  }

  const registration = await getRegistration();
  const subscription = await registration.pushManager.getSubscription();
  return subscription && Notification.permission === 'granted' ? 'enabled' : 'disabled';
}

export async function enablePushNotifications() {
  if (!isPushSupported()) {
    throw new Error('Push notifications are not supported by this browser.');
  }
  const publicKey = getPublicKey();
  if (!publicKey) {
    throw new Error('Push notifications are not available in this environment.');
  }

  const permission = Notification.permission === 'default'
    ? await Notification.requestPermission()
    : Notification.permission;
  if (permission !== 'granted') {
    throw new Error('Push permission was not granted. You can enable it later in your browser settings.');
  }

  const registration = await getRegistration();
  let subscription = await registration.pushManager.getSubscription();
  let createdSubscription = false;
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: toApplicationServerKey(publicKey),
    });
    createdSubscription = true;
  }

  try {
    await saveSubscription(subscription);
    return subscription;
  } catch (error) {
    if (createdSubscription) {
      await subscription.unsubscribe().catch(() => {});
    }
    throw error;
  }
}

export async function disablePushNotifications() {
  if (!isPushSupported()) {
    return;
  }

  const registration = await getRegistration();
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    return;
  }

  const endpoint = subscription.endpoint;
  const { response, data } = await fetchWrapper.delete(`${config.apis.api.url}/notification/push-subscriptions`, {
    injectToken: true,
    body: { endpoint },
  });
  if (!response.ok) {
    throw new Error(data?.error_message || 'Unable to disable push notifications.');
  }
  await subscription.unsubscribe();
}

export { isPushSupported };
